/**
 * Shared helper for rendering rich-text HTML safely and cleanly.
 *
 * SECURITY: all HTML is sanitized with DOMPurify before render to prevent
 * stored XSS (e.g. an interpreter injecting <script> or <img onerror> into a
 * self-edited bio, or malicious markup in news/event bodies). DOMPurify strips
 * scripts, event handlers and other dangerous vectors while keeping safe
 * formatting tags.
 *
 * Text pasted from PDF/Word frequently arrives with non-breaking spaces
 * (`&nbsp;` / U+00A0) between words. Those characters glue words together so
 * the browser can't break the line at them — the paragraph then overflows its
 * container and `overflow-wrap` chops a word in half WITHOUT a hyphen
 * (e.g. "diversidad p / rofesional"). Normalising them to regular spaces lets
 * the text wrap (and hyphenate) normally.
 */
import DOMPurify from 'isomorphic-dompurify';

export function normalizeHtml(html) {
  const normalized = (html || '').replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
  return DOMPurify.sanitize(normalized, { ADD_ATTR: ['target', 'rel'] });
}

// Detects whether a stored string already contains HTML markup or entities
// (vs pure plain text). Entity-only content (e.g. "La&nbsp;asociación") must be
// rendered as HTML so &nbsp;/&quot; don't show up literally.
export function looksLikeHtml(s) {
  const str = s || '';
  return /<\/?[a-z][\s\S]*>/i.test(str) || /&(nbsp|amp|quot|lt|gt|apos|#\d+);/i.test(str);
}

// Converts stored content into safe HTML for a WYSIWYG editor. Normalizes
// non-breaking spaces to regular spaces first (so Quill doesn't carry them
// forward). If it already contains tags it is returned as-is (nbsp-normalized).
// Pure plain text is escaped and wrapped in paragraphs, keeping line breaks.
export function plainToHtml(text) {
  if (!text) return '';
  let s = String(text).replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
  if (/<\/?[a-z][\s\S]*>/i.test(s)) return s;
  s = s.replace(/&quot;/gi, '"').replace(/&#39;/g, "'").replace(/&apos;/gi, "'").replace(/&amp;/gi, '&');
  const esc = (t) => t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return s
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}
